/* prettier-ignore-file */
/**
 * Edit Booking Wizard — pure logic.
 *
 * The design principle (and how it differs from EditBooking.js): the booking is
 * never patched incrementally. The original document is FROZEN, edits build a
 * DRAFT (the same shape the booking wizard uses), and every derived number is
 * recomputed wholesale from that draft with computeRentalQuote — the exact same
 * pricing engine that priced the booking at creation. Money to move is then a
 * single subtraction: (new total) − (what has been collected so far).
 *
 * Price honoring: retained items keep the rate they were sold at. This is done by
 * pricing the draft against an EFFECTIVE catalog — the live catalog with each
 * product's price replaced by the booking's stored pricePaid for items already on
 * the booking (buildEffectiveCatalogs). Items newly added in the edit price at
 * today's catalog rate. Without this, a catalog price change since booking would
 * silently reprice the whole order the moment any edit is made.
 *
 * ALL MONEY IN CENTS except where a field is explicitly *Dollars — those exist
 * only for the sendEditedBookingNotification payload, whose email template
 * renders `changes.netAmount` and item prices with .toFixed(2) as dollars.
 */

import { Timestamp } from "firebase/firestore";
import { capitalizeName } from "./formatting";
import {
  computeRentalQuote,
  getRentalDays,
  parseRentalDate,
} from "./rentalPricing";
import { buildLineItems } from "./createWizardBooking";
import { computeAvailabilityForRange } from "./rentalAvailability";

// ---------------------------------------------------------------------------
// Draft loading
// ---------------------------------------------------------------------------

const toIso = (value) => {
  const date = parseRentalDate(value);
  return date ? date.toISOString() : null;
};

/**
 * Booking document -> wizard draft (the useNewBookingStore field shape, so the
 * existing Step components work unchanged).
 *
 * selectedBikes is keyed by product NAME (the store convention buildLineItems
 * resolves against), so each item maps to the effective catalog's CURRENT name —
 * a product renamed since booking would otherwise miss the catalog lookup and
 * silently price at $0.
 *
 * communityName is not stored separately on bookings (creation folds it into
 * addressNotes), so it loads empty; the stored addressNotes keep any folded line.
 */
export const bookingToDraft = (booking, effectiveProducts1 = null) => {
  const selectedBikes = {};
  (booking.product1 || []).forEach((item) => {
    if ((item.quantity || 0) <= 0) return;
    const catalogProduct = (effectiveProducts1 || []).find((product) =>
      matchesBookingItem(item, product)
    );
    selectedBikes[catalogProduct?.name || item.name] = item.quantity;
  });

  const selectedAddOns = {};
  (booking.product2 || []).forEach((item) => {
    if ((item.quantity || 0) <= 0) return;
    const option = item.option || "per-booking";
    selectedAddOns[item.id] =
      option === "per-bike" ? true : { quantity: item.quantity };
  });

  return {
    startDate: toIso(booking.startDate),
    endDate: toIso(booking.endDate),
    currentMonth: toIso(booking.startDate) || new Date().toISOString(),
    selectedBikes,
    selectedAddOns,
    dailyRateOverrideCents: Number.isFinite(booking.dailyRateOverride)
      ? booking.dailyRateOverride
      : null,
    deliveryNeeded: !!booking.delivery,
    homePickupNeeded: !!booking.homePickup,
    inStorePickup: !(booking.delivery && booking.homePickup),
    gatedCommunity: !!booking.gatedCommunity,
    communityName: "",
    addressNotes: booking.addressNotes || "",
    addressInput: booking.deliveryAddress || "",
    // The stored address was validated when the booking was made; only a changed
    // address needs re-confirming.
    addressConfirmed: !!booking.deliveryAddress,
    // Stored lower-case on purpose (see buildBookingData below); show it the way
    // a person writes it. The change check is case-insensitive, so this alone
    // never marks the booking as edited.
    firstName: capitalizeName(booking.firstName),
    lastName: capitalizeName(booking.lastName),
    customerEmail: booking.customerEmail || "",
    customerPhone: booking.customerPhone || "",
    miles: booking.numMiles != null ? Number(booking.numMiles) : null,
  };
};

// ---------------------------------------------------------------------------
// Effective catalogs — price honoring
// ---------------------------------------------------------------------------

const matchesBookingItem = (item, product) =>
  item.id === product.id ||
  (item.id && product.name && item.id === product.name) ||
  (item.name && product.name && item.name === product.name);

/**
 * Live catalogs with sold prices locked in for items already on the booking.
 *
 *  - product1: pricePaid is the per-unit DAILY rate -> replaces catalog price.
 *  - product2 per-booking / user-selection: pricePaid is the unit price -> replaces.
 *  - product2 per-bike: pricePaid is a TOTAL (price × bikes at sale time), which
 *    cannot be unpacked reliably, so those stay at catalog price.
 *  - Items on the booking but no longer in the catalog get a synthetic entry
 *    (untracked inventory) so they can still be priced, displayed and removed.
 */
export const buildEffectiveCatalogs = ({ booking, products1, products2 }) => {
  const effective1 = (products1 || []).map((product) => {
    const sold = (booking.product1 || []).find((item) => matchesBookingItem(item, product));
    return sold && Number.isFinite(sold.pricePaid)
      ? { ...product, price: sold.pricePaid }
      : product;
  });
  (booking.product1 || []).forEach((item) => {
    if ((item.quantity || 0) <= 0) return;
    const inCatalog = effective1.some((product) => matchesBookingItem(item, product));
    if (!inCatalog) {
      effective1.push({
        id: item.id || item.name,
        name: item.name || item.id,
        price: Number.isFinite(item.pricePaid) ? item.pricePaid : 0,
        quantity: null, // untracked -> availability treats as unlimited
        missingFromCatalog: true,
      });
    }
  });

  const effective2 = (products2 || []).map((product) => {
    const sold = (booking.product2 || []).find((item) => matchesBookingItem(item, product));
    const option = product.option || "per-booking";
    if (sold && Number.isFinite(sold.pricePaid) && option !== "per-bike") {
      return { ...product, price: sold.pricePaid };
    }
    return product;
  });
  (booking.product2 || []).forEach((item) => {
    if ((item.quantity || 0) <= 0) return;
    const inCatalog = effective2.some((product) => matchesBookingItem(item, product));
    if (!inCatalog) {
      effective2.push({
        id: item.id || item.name,
        name: item.name || item.id,
        price: Number.isFinite(item.pricePaid) ? item.pricePaid : 0,
        option: item.option || "per-booking",
        inventory: null,
        missingFromCatalog: true,
      });
    }
  });

  return { products1: effective1, products2: effective2 };
};

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

/** Line items + full quote for the draft, priced against the effective catalogs. */
export const deriveEditQuote = ({ draft, effectiveProducts1, effectiveProducts2, settings }) => {
  const lineItems = buildLineItems({
    selectedBikes: draft.selectedBikes,
    selectedAddOns: draft.selectedAddOns,
    products1: effectiveProducts1,
    products2: effectiveProducts2,
    dailyRateOverrideCents: draft.dailyRateOverrideCents,
  });

  const quote = computeRentalQuote({
    startDate: draft.startDate,
    endDate: draft.endDate,
    product1: lineItems.product1,
    product2: lineItems.product2,
    products1Catalog: effectiveProducts1,
    products2Catalog: effectiveProducts2,
    settings,
    delivery: draft.deliveryNeeded,
    homePickup: draft.homePickupNeeded,
    miles: draft.miles,
    dailyRateOverrideCents: draft.dailyRateOverrideCents,
  });

  return { lineItems, quote };
};

// ---------------------------------------------------------------------------
// Change detection
// ---------------------------------------------------------------------------

const sameDay = (a, b) => {
  const da = parseRentalDate(a);
  const db = parseRentalDate(b);
  if (!da && !db) return true;
  if (!da || !db) return false;
  da.setHours(0, 0, 0, 0);
  db.setHours(0, 0, 0, 0);
  return da.getTime() === db.getTime();
};

const itemMapById = (items) => {
  const map = {};
  (items || []).forEach((item) => {
    if (!item) return;
    map[item.id || item.name] = item;
  });
  return map;
};

/** Which sections of the booking the draft changes. Drives hub badges and the commit. */
export const summarizeDraftChanges = ({ booking, draft, lineItems }) => {
  const datesChanged =
    !sameDay(booking.startDate, draft.startDate) || !sameDay(booking.endDate, draft.endDate);

  const oldP1 = itemMapById(booking.product1);
  const newP1 = itemMapById(lineItems.product1);
  const oldP2 = itemMapById(booking.product2);
  const newP2 = itemMapById(lineItems.product2);
  const idsOf = (a, b) => [...new Set([...Object.keys(a), ...Object.keys(b)])];
  const itemsChanged =
    idsOf(oldP1, newP1).some(
      (id) => (oldP1[id]?.quantity || 0) !== (newP1[id]?.quantity || 0)
    ) ||
    idsOf(oldP2, newP2).some(
      (id) => (oldP2[id]?.quantity || 0) !== (newP2[id]?.quantity || 0)
    );

  const originalOverride = Number.isFinite(booking.dailyRateOverride)
    ? booking.dailyRateOverride
    : null;
  const rateOverrideChanged = originalOverride !== (draft.dailyRateOverrideCents ?? null);

  const transportChanged =
    !!booking.delivery !== !!draft.deliveryNeeded ||
    !!booking.homePickup !== !!draft.homePickupNeeded ||
    (booking.deliveryAddress || "") !== (draft.addressInput || "").trim() ||
    (booking.addressNotes || "") !== (draft.addressNotes || "") ||
    !!booking.gatedCommunity !== !!draft.gatedCommunity ||
    !!draft.communityName;

  const customerChanged =
    (booking.firstName || "").trim().toLowerCase() !== (draft.firstName || "").trim().toLowerCase() ||
    (booking.lastName || "").trim().toLowerCase() !== (draft.lastName || "").trim().toLowerCase() ||
    (booking.customerEmail || "").trim().toLowerCase() !== (draft.customerEmail || "").trim().toLowerCase() ||
    (booking.customerPhone || "").replace(/\D/g, "") !== (draft.customerPhone || "").replace(/\D/g, "");

  return {
    datesChanged,
    itemsChanged,
    rateOverrideChanged,
    transportChanged,
    customerChanged,
    // Anything that can move money. Customer info alone deliberately does not
    // reprice: recomputing totals for a phone-number fix could surface a charge or
    // refund caused purely by settings/catalog drift since the booking was made.
    priceAffecting: datesChanged || itemsChanged || rateOverrideChanged || transportChanged,
    anyChange:
      datesChanged || itemsChanged || rateOverrideChanged || transportChanged || customerChanged,
  };
};

// ---------------------------------------------------------------------------
// Cancellation (date-change) penalty
// ---------------------------------------------------------------------------

const DEFAULT_CANCELLATION_WINDOWS = [
  { hours: 48, percentPenalty: 0, daysPenalty: 0 },
  { hours: 24, percentPenalty: 25, daysPenalty: 1 },
  { hours: 0, percentPenalty: 50, daysPenalty: 2 },
];

/**
 * Fee for shrinking the rental, per the store's cancellation windows
 * (settings.cancellationPolicyWindows). Ports EditBooking.js:4002-4238.
 *
 * Base amount = (original paid total / original days) × days lost. The window is
 * picked by hours remaining until the ORIGINAL start (at 11 AM), so a last-minute
 * shortening costs more than one made a week out. Extending or shifting without
 * shrinking carries no fee.
 */
export const computeDateChangeFeeCents = ({ booking, draft, settings, now = new Date() }) => {
  const none = { feeCents: 0, daysLost: 0, percentPenalty: 0, windowHours: null, penaltyType: null };

  const originalDays = getRentalDays(booking.startDate, booking.endDate);
  const newDays = getRentalDays(draft.startDate, draft.endDate);
  if (!originalDays || !newDays || newDays >= originalDays) return none;

  const daysLost = originalDays - newDays;

  const origStart = parseRentalDate(booking.startDate);
  if (!origStart) return none;
  const startAt11 = new Date(origStart);
  startAt11.setHours(11, 0, 0, 0);
  const hoursUntilStart = (startAt11.getTime() - now.getTime()) / (1000 * 60 * 60);

  const policy = settings?.cancellationPolicyWindows || {};
  const windows = (policy.windows && policy.windows.length > 0)
    ? policy.windows
    : DEFAULT_CANCELLATION_WINDOWS;
  const penaltyType = policy.penaltyType || "percentage";

  const sorted = [...windows].sort((a, b) => (b.hours || 0) - (a.hours || 0));
  let window = sorted.find((w) => hoursUntilStart >= (w.hours || 0));
  if (!window) window = sorted[sorted.length - 1];

  const paidTotalCents =
    booking.payments?.[0]?.costBreakdown?.totalCharged ?? booking.totalAmount ?? 0;
  const dailyRateCents = originalDays > 0 ? paidTotalCents / originalDays : 0;
  const grossLostCents = dailyRateCents * daysLost;

  let feeCents = 0;
  if (penaltyType === "days") {
    feeCents = Math.min(dailyRateCents * (window.daysPenalty || 0), grossLostCents);
  } else {
    feeCents = grossLostCents * ((window.percentPenalty || 0) / 100);
  }

  return {
    feeCents: Math.max(0, Math.round(feeCents)),
    daysLost,
    percentPenalty: window.percentPenalty || 0,
    daysPenalty: window.daysPenalty || 0,
    windowHours: window.hours || 0,
    penaltyType,
    hoursUntilStart,
  };
};

// ---------------------------------------------------------------------------
// Settlement — what money moves
// ---------------------------------------------------------------------------

/**
 * The single subtraction at the heart of the wizard.
 *
 * booking.totalAmount is maintained by every flow as "net amount collected for
 * this booking", so the delta is (new quote total) − totalAmount, adjusted by the
 * date-change fee. Refunds are further reduced by the store's refund processing
 * fee unless the admin overrides the amount.
 *
 * The stored total after commit always equals prevTotal ± money actually moved,
 * so paid − refunded === totalAmount stays true no matter what overrides applied.
 *
 * Unpaid bookings (manual "book directly" entries with an empty payments array)
 * are the exception: nothing has been collected, so there is nothing to charge
 * against or refund from. Their total simply REPRICES to the new quote (plus any
 * date-change fee they now owe) and no payment is processed.
 *
 * @param {Object} refundOverride { mode: "auto"|"percent"|"custom", percent, customCents }
 * @param {number} collectedCents net money actually collected so far (paid − refunded)
 */
/**
 * Staff override for money the customer still OWES, clamped to [0, base].
 *
 * Downward only, deliberately: an override may forgive part or all of what the
 * reprice produced, but must never invent a charge larger than the booking
 * justifies -- that would be a price rise the customer never agreed to, applied
 * from a text box. Zero is a legitimate value and means "waive it entirely".
 *
 * The clamp is a safety net, not the UX. The wizard blocks committing an
 * out-of-range figure, so the number typed and the number charged can never
 * silently disagree.
 */
const applyOwedOverride = (baseCents, owedOverride) => {
  if (!owedOverride || !Number.isFinite(owedOverride.customCents)) return baseCents;
  return Math.min(baseCents, Math.max(0, Math.round(owedOverride.customCents)));
};

export const computeSettlement = ({
  booking,
  quote,
  priceAffecting,
  dateChangeFeeCents = 0,
  refundProcessingFeePercent = 0,
  refundOverride = null,
  owedOverride = null,
  collectedCents = null,
}) => {
  const prevTotalCents = Math.round(booking.totalAmount || 0);

  if (!priceAffecting) {
    return {
      kind: "none",
      priceAffecting: false,
      prevTotalCents,
      newQuoteTotalCents: prevTotalCents,
      chargeCents: 0,
      refundCents: 0,
      dateChangeFeeCents: 0,
      processingFeeCents: 0,
      newTotalAmountCents: prevTotalCents,
    };
  }

  const newQuoteTotalCents = quote.finalTotalCents;

  if (collectedCents != null && collectedCents <= 0) {
    // Nothing was ever collected, so totalAmount here IS the amount owed and the
    // override rewrites it directly. No money moves either way.
    const baseOwedCents = newQuoteTotalCents + dateChangeFeeCents;
    return {
      kind: "reprice",
      priceAffecting: true,
      prevTotalCents,
      newQuoteTotalCents,
      chargeCents: 0,
      refundCents: 0,
      dateChangeFeeCents,
      processingFeeCents: 0,
      baseOwedCents,
      newTotalAmountCents: applyOwedOverride(baseOwedCents, owedOverride),
    };
  }

  const rawDeltaCents = newQuoteTotalCents - prevTotalCents;
  const adjustedDeltaCents = rawDeltaCents + dateChangeFeeCents;

  if (adjustedDeltaCents > 0) {
    const baseChargeCents = adjustedDeltaCents;
    const chargeCents = applyOwedOverride(baseChargeCents, owedOverride);
    return {
      // Waiving the whole difference leaves no money to move, which is exactly
      // "none" -- same shape the refund branch collapses to at zero.
      kind: chargeCents > 0 ? "charge" : "none",
      priceAffecting: true,
      prevTotalCents,
      newQuoteTotalCents,
      chargeCents,
      // Kept even when the override collapses kind to "none", so the wizard can
      // still tell a waived charge from a booking that never owed anything and
      // keep the override control on screen. Without it the control would vanish
      // the instant it was set to 0 and could never be undone.
      baseChargeCents,
      refundCents: 0,
      dateChangeFeeCents,
      processingFeeCents: 0,
      // The settlement invariant: totalAmount tracks money actually moved, so it
      // advances by what is really charged, not by what the reprice wanted.
      newTotalAmountCents: prevTotalCents + chargeCents,
    };
  }

  if (adjustedDeltaCents === 0) {
    return {
      kind: "none",
      priceAffecting: true,
      prevTotalCents,
      newQuoteTotalCents,
      chargeCents: 0,
      refundCents: 0,
      dateChangeFeeCents,
      processingFeeCents: 0,
      newTotalAmountCents: prevTotalCents,
    };
  }

  const baseRefundCents = -adjustedDeltaCents;
  let processingFeeCents = 0;
  let refundCents;

  if (refundOverride?.mode === "custom" && Number.isFinite(refundOverride.customCents)) {
    refundCents = Math.max(0, Math.round(refundOverride.customCents));
  } else if (refundOverride?.mode === "percent" && Number.isFinite(refundOverride.percent)) {
    refundCents = Math.max(
      0,
      Math.round(baseRefundCents * (refundOverride.percent / 100))
    );
  } else {
    processingFeeCents = Math.round(baseRefundCents * (refundProcessingFeePercent / 100));
    refundCents = Math.max(0, baseRefundCents - processingFeeCents);
  }

  return {
    kind: refundCents > 0 ? "refund" : "none",
    priceAffecting: true,
    prevTotalCents,
    newQuoteTotalCents,
    chargeCents: 0,
    refundCents,
    baseRefundCents,
    dateChangeFeeCents,
    processingFeeCents,
    newTotalAmountCents: prevTotalCents - refundCents,
  };
};

// ---------------------------------------------------------------------------
// Payment profile & refund allocation
// ---------------------------------------------------------------------------

const isCardPayment = (payment) =>
  !!(
    payment.stripePaymentIntentId ||
    payment.paymentIntentId ||
    payment.stripeSessionId ||
    payment.method === "saved_card" ||
    payment.method === "stripe_checkout"
  );

/** payments[].amount is cents in every current writer. */
const paymentCents = (payment) => {
  if (payment.amount != null && Number.isFinite(Number(payment.amount))) {
    return Math.round(Number(payment.amount));
  }
  return Math.round(payment.costBreakdown?.totalCharged || 0);
};

/**
 * refunds[] units are messy in legacy data: entries written client-side carry
 * `amount` (cents); entries written by processRefund/processRefundOnly carry
 * `refundAmount` (dollars). Read accordingly.
 */
const refundCentsOf = (refund) => {
  if (refund.amount != null && Number.isFinite(Number(refund.amount))) {
    return Math.round(Number(refund.amount));
  }
  if (refund.refundAmount != null && Number.isFinite(Number(refund.refundAmount))) {
    return Math.round(Number(refund.refundAmount) * 100);
  }
  return 0;
};

const isCashRefund = (refund) => refund.method === "cash";

/**
 * How this booking has been paid, what remains refundable per method, and the
 * saved card available for further charges.
 */
export const buildPaymentProfile = (booking) => {
  const payments = Array.isArray(booking.payments) ? booking.payments : [];
  const refunds = Array.isArray(booking.refunds) ? booking.refunds : [];

  let cardPaidCents = 0;
  let cashPaidCents = 0;
  payments.forEach((payment) => {
    if (!payment) return;
    const cents = paymentCents(payment);
    if (payment.method === "cash") cashPaidCents += cents;
    else if (isCardPayment(payment)) cardPaidCents += cents;
    else cardPaidCents += cents; // legacy entries without markers were card flows
  });

  let cardRefundedCents = 0;
  let cashRefundedCents = 0;
  refunds.forEach((refund) => {
    if (!refund) return;
    const cents = refundCentsOf(refund);
    if (isCashRefund(refund)) cashRefundedCents += cents;
    else cardRefundedCents += cents; // every legacy refund writer was Stripe
  });

  // Most recent payment holding a reusable card.
  let savedCard = null;
  for (let i = payments.length - 1; i >= 0; i -= 1) {
    const payment = payments[i];
    if (payment?.stripeCustomerId && payment?.cardInfo?.last4) {
      savedCard = { last4: payment.cardInfo.last4, brand: payment.cardInfo.brand || "" };
      break;
    }
  }

  const hasStripePaymentIntent = payments.some(
    (payment) => payment?.stripePaymentIntentId || payment?.paymentIntentId || payment?.stripeSessionId
  );

  return {
    cardPaidCents,
    cashPaidCents,
    cardRefundedCents,
    cashRefundedCents,
    cardAvailableCents: Math.max(0, cardPaidCents - cardRefundedCents),
    cashAvailableCents: Math.max(0, cashPaidCents - cashRefundedCents),
    savedCard,
    hasStripePaymentIntent,
    hasCardInfo: payments.some((payment) => payment?.cardInfo?.last4),
  };
};

/**
 * Split a refund between the card and the till.
 *
 * Rule (fixed): money paid by card goes back to the card, and only money that
 * arrived as cash may leave as cash. Card-first by default; preferCash flips the
 * order for bookings that were paid both ways. shortfallCents > 0 means the
 * requested refund exceeds everything collected — the caller must block or lower it.
 */
export const allocateRefund = ({ profile, refundCents, preferCash = false }) => {
  let remaining = Math.max(0, Math.round(refundCents));
  let cardCents = 0;
  let cashCents = 0;

  const takeCard = () => {
    cardCents = Math.min(remaining, profile.cardAvailableCents);
    remaining -= cardCents;
  };
  const takeCash = () => {
    cashCents = Math.min(remaining, profile.cashAvailableCents);
    remaining -= cashCents;
  };

  if (preferCash) {
    takeCash();
    takeCard();
  } else {
    takeCard();
    takeCash();
  }

  return { cardCents, cashCents, shortfallCents: remaining };
};

// ---------------------------------------------------------------------------
// Notification payload (standard edited-booking email + SMS)
// ---------------------------------------------------------------------------

const centsToDollarsNumber = (cents) => Math.round(cents) / 100;

/**
 * The `changes` object and its companions for the sendEditedBookingNotification
 * cloud function. Item prices here are DOLLARS PER UNIT: the email renders each
 * line as "{quantity}x {name} - ${quantity × price}". Lines are descriptive
 * (pre-tax), exactly as the old edit page produced them; the authoritative money
 * figure is netAmount, which is set to what is actually charged or refunded.
 */
export const buildNotificationChanges = ({
  booking,
  draft,
  lineItems,
  quote,
  settlement,
  effectiveProducts2,
}) => {
  const addedItems = [];
  const removedItems = [];

  const oldDays = getRentalDays(booking.startDate, booking.endDate) || 1;
  const newDays = quote.days || 1;

  // --- Product1 quantity diffs (daily rate × rental length per unit) ---
  const oldP1 = itemMapById(booking.product1);
  const newP1 = itemMapById(lineItems.product1);
  [...new Set([...Object.keys(oldP1), ...Object.keys(newP1)])].forEach((id) => {
    const oldItem = oldP1[id];
    const newItem = newP1[id];
    const oldQty = oldItem?.quantity || 0;
    const newQty = newItem?.quantity || 0;
    const diff = newQty - oldQty;
    if (diff > 0) {
      const dailyCents = draft.dailyRateOverrideCents ?? newItem.pricePaid ?? 0;
      addedItems.push({
        id,
        type: "product1",
        name: newItem.name,
        quantity: diff,
        price: centsToDollarsNumber(dailyCents * newDays),
      });
    } else if (diff < 0) {
      const dailyCents = oldItem.pricePaid ?? 0;
      removedItems.push({
        id,
        type: "product1",
        name: oldItem.name,
        quantity: -diff,
        price: centsToDollarsNumber(dailyCents * oldDays),
      });
    }
  });

  // --- Product2 quantity diffs, priced by their billing option ---
  const oldP2 = itemMapById(booking.product2);
  const newP2 = itemMapById(lineItems.product2);
  [...new Set([...Object.keys(oldP2), ...Object.keys(newP2)])].forEach((id) => {
    const oldItem = oldP2[id];
    const newItem = newP2[id];
    const oldQty = oldItem?.quantity || 0;
    const newQty = newItem?.quantity || 0;
    const diff = newQty - oldQty;
    if (diff === 0) return;

    const option =
      newItem?.option ||
      oldItem?.option ||
      (effectiveProducts2 || []).find((p) => p.id === id)?.option ||
      "per-booking";

    if (diff > 0) {
      let unitCents = newItem.pricePaid || 0; // per-bike items store the total paid
      if (option === "user-selection") unitCents *= newDays;
      addedItems.push({
        id,
        type: "product2",
        name: newItem.name,
        quantity: option === "per-bike" ? 1 : diff,
        price: centsToDollarsNumber(unitCents),
      });
    } else {
      let unitCents = oldItem.pricePaid || 0;
      if (option === "user-selection") unitCents *= oldDays;
      removedItems.push({
        id,
        type: "product2",
        name: oldItem.name,
        quantity: option === "per-bike" ? 1 : -diff,
        price: centsToDollarsNumber(unitCents),
      });
    }
  });

  // --- Duration change on retained bikes (adds/removes only cover new/dropped units) ---
  if (newDays !== oldDays) {
    let retainedDailyCents = 0;
    Object.keys(newP1).forEach((id) => {
      const newItem = newP1[id];
      const oldItem = oldP1[id];
      const retainedQty = Math.min(newItem?.quantity || 0, oldItem?.quantity || 0);
      if (retainedQty > 0) {
        const dailyCents =
          draft.dailyRateOverrideCents ?? newItem.pricePaid ?? oldItem.pricePaid ?? 0;
        retainedDailyCents += dailyCents * retainedQty;
      }
    });
    const deltaDays = Math.abs(newDays - oldDays);
    const amount = centsToDollarsNumber(retainedDailyCents * deltaDays);
    if (retainedDailyCents > 0) {
      const entry = {
        name:
          newDays > oldDays
            ? `Rental extension (${deltaDays} extra day${deltaDays === 1 ? "" : "s"})`
            : `Rental shortened (${deltaDays} fewer day${deltaDays === 1 ? "" : "s"})`,
        quantity: 1,
        price: amount,
      };
      if (newDays > oldDays) addedItems.push(entry);
      else removedItems.push(entry);
    }
  }

  // --- Transportation legs added / removed ---
  const legLabel = (delivery, pickup) =>
    [delivery && "Delivery", pickup && "Home Pickup"].filter(Boolean).join(" & ");
  const addedDelivery = draft.deliveryNeeded && !booking.delivery;
  const removedDelivery = !draft.deliveryNeeded && booking.delivery;
  const addedPickup = draft.homePickupNeeded && !booking.homePickup;
  const removedPickup = !draft.homePickupNeeded && booking.homePickup;
  if (addedDelivery || addedPickup) {
    const cents =
      (addedDelivery ? quote.deliveryWithDiscountCents : 0) +
      (addedPickup ? quote.pickupWithDiscountCents : 0);
    addedItems.push({
      name: `${legLabel(addedDelivery, addedPickup)} Added`,
      quantity: 1,
      price: centsToDollarsNumber(cents),
    });
  }
  if (removedDelivery || removedPickup) {
    const cents =
      (removedDelivery ? booking.deliveryWithDiscount || booking.deliveryCostCents || 0 : 0) +
      (removedPickup ? booking.pickupWithDiscount || booking.pickupCostCents || 0 : 0);
    removedItems.push({
      name: `${legLabel(removedDelivery, removedPickup)} Removed`,
      quantity: 1,
      price: centsToDollarsNumber(cents),
    });
  }

  // --- Date change fee, so the kept amount is visible in the email ---
  if (settlement.dateChangeFeeCents > 0) {
    addedItems.push({
      name: "Date change fee",
      quantity: 1,
      price: centsToDollarsNumber(settlement.dateChangeFeeCents),
    });
  }

  const additionsTotal = addedItems.reduce(
    (sum, item) => sum + item.quantity * item.price,
    0
  );
  const removalsTotal = removedItems.reduce(
    (sum, item) => sum + item.quantity * item.price,
    0
  );

  const netAmountDollars = centsToDollarsNumber(
    settlement.kind === "charge" ? settlement.chargeCents : settlement.refundCents
  );

  return {
    additionsTotal,
    removalsTotal,
    addedItems,
    removedItems,
    netAmount: netAmountDollars,
    isNetCharge: settlement.kind === "charge",
    isNetRefund: settlement.kind === "refund",
    days: newDays,
  };
};

/** {products1:{id:qty}, products2:{id:qty}} for the editHistory audit entry. */
export const buildQuantityChanges = ({ booking, lineItems }) => {
  const result = { products1: {}, products2: {} };
  const collect = (oldItems, newItems, bucket) => {
    const oldMap = itemMapById(oldItems);
    const newMap = itemMapById(newItems);
    [...new Set([...Object.keys(oldMap), ...Object.keys(newMap)])].forEach((id) => {
      const oldQty = oldMap[id]?.quantity || 0;
      const newQty = newMap[id]?.quantity || 0;
      if (oldQty !== newQty) bucket[id] = newQty;
    });
  };
  collect(booking.product1, lineItems.product1, result.products1);
  collect(booking.product2, lineItems.product2, result.products2);
  return result;
};

// ---------------------------------------------------------------------------
// Money entries — canonical shapes (cents), mirroring the Stripe webhook's
// payment entry (functions/index.js:3916) and buildCashPaymentEntry.
// ---------------------------------------------------------------------------

/**
 * Cost breakdown for an EDIT charge entry.
 *
 * INVARIANT: costBreakdown.totalCharged must equal the money THIS entry moved —
 * Bookings.js:743 computes "total paid" by summing costBreakdown.totalCharged
 * (falling back to amount) across every entry, so putting the full new booking
 * total here would double-count it. Component fields stay 0, exactly like the
 * Stripe webhook's edit_charge entries; the new configuration's full breakdown
 * lives on the booking document itself (productPriceAmount, salesTaxAmount, …).
 */
const editChargeCostBreakdown = (amountCents) => ({
  productCost: 0,
  deliveryCost: 0,
  pickupCost: 0,
  deliveryWithDiscount: 0,
  pickupWithDiscount: 0,
  durationDiscount: { discountPercent: 0, discountName: "", discountCents: 0 },
  deliveryDiscount: { discountPercent: 0, discountName: "", discountCents: 0 },
  salesTaxCharged: 0,
  totalCharged: amountCents,
});

const entryId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

/** Additional charge collected on the saved card via chargeSavedCard. */
export const buildCardChargeEntry = ({ amountCents, paymentIntentId, sourcePayment, lineItems }) => ({
  paymentId: entryId("editpay"),
  type: "edit_charge",
  method: "saved_card",
  timestamp: Timestamp.now(), // serverTimestamp() is illegal inside arrays

  stripeCustomerId: sourcePayment?.stripeCustomerId || null,
  stripeSessionId: null,
  stripePaymentIntentId: paymentIntentId || null,
  stripePaymentMethodId: sourcePayment?.stripePaymentMethodId || null,
  cardInfo: sourcePayment?.cardInfo || null,

  amount: amountCents,
  currency: "usd",
  description: "Booking edit additional charge",

  items: { product1: lineItems.product1, product2: lineItems.product2 },
  costBreakdown: editChargeCostBreakdown(amountCents),
});

/** Additional charge taken in cash at the register. */
export const buildCashChargeEntry = ({ amountCents, lineItems }) => ({
  paymentId: entryId("editcash"),
  type: "edit_charge",
  method: "cash",
  timestamp: Timestamp.now(),

  stripeCustomerId: null,
  stripeSessionId: null,
  stripePaymentIntentId: null,
  stripePaymentMethodId: null,
  cardInfo: null,

  amount: amountCents,
  currency: "usd",
  description: "Booking edit additional charge (cash)",

  items: { product1: lineItems.product1, product2: lineItems.product2 },
  costBreakdown: editChargeCostBreakdown(amountCents),
});

/**
 * Refund entries. `amount` is CENTS — ViewBooking.js:978 reads amount ?? refundAmount
 * and divides by 100, so this displays correctly.
 */
export const buildCardRefundEntry = ({ amountCents, stripeRefundId, paymentIntentId, reason }) => ({
  refundId: stripeRefundId || entryId("editrefund"),
  type: "edit_refund",
  method: "card",
  timestamp: Timestamp.now(),
  amount: amountCents,
  currency: "usd",
  reason: reason || "booking_edit",
  stripeRefundId: stripeRefundId || null,
  stripePaymentIntentId: paymentIntentId || null,
});

export const buildCashRefundEntry = ({ amountCents, reason }) => ({
  refundId: entryId("editcashrefund"),
  type: "edit_refund",
  method: "cash",
  timestamp: Timestamp.now(),
  amount: amountCents,
  currency: "usd",
  reason: reason || "booking_edit",
  stripeRefundId: null,
  stripePaymentIntentId: null,
});

// ---------------------------------------------------------------------------
// The updated booking document
// ---------------------------------------------------------------------------

/**
 * Every derived field on the booking, recomputed from the draft. This is the ONE
 * write that changes the booking's content; nothing is patched incrementally.
 * Money entries (payments/refunds arrayUnion) are appended by the caller.
 */
export const buildUpdatedBookingFields = ({ booking, draft, lineItems, quote, settlement }) => {
  const needsAddress = draft.deliveryNeeded || draft.homePickupNeeded;

  // Fold a newly entered community name into the notes exactly as booking
  // creation does; existing notes already carry any previous fold.
  const notesWithCommunity =
    draft.gatedCommunity && draft.communityName
      ? `${draft.addressNotes || ""}${draft.addressNotes ? "\n" : ""}Gated community: ${draft.communityName}\n`
      : draft.addressNotes || "";

  const fields = {
    customerEmail: (draft.customerEmail || "").trim().toLowerCase(),
    customerPhone: (draft.customerPhone || "").replace(/\D/g, ""),
    firstName: (draft.firstName || "").trim().toLowerCase(),
    lastName: (draft.lastName || "").trim().toLowerCase(),

    startDate: Timestamp.fromDate(new Date(draft.startDate)),
    endDate: Timestamp.fromDate(new Date(draft.endDate)),

    product1: lineItems.product1,
    product2: lineItems.product2,

    delivery: !!draft.deliveryNeeded,
    homePickup: !!draft.homePickupNeeded,
    inStorePickup: !(draft.deliveryNeeded && draft.homePickupNeeded),
    deliveryAddress: needsAddress && draft.addressInput ? draft.addressInput.trim() : "",
    gatedCommunity: !!draft.gatedCommunity,
    addressNotes: notesWithCommunity,
    numMiles: draft.miles != null ? parseFloat(Number(draft.miles).toFixed(1)) : null,

    dailyRateOverride: Number.isFinite(draft.dailyRateOverrideCents)
      ? draft.dailyRateOverrideCents
      : null,

    updatedAt: Timestamp.now(),
  };

  // Cost fields only move when the edit was price-affecting; a customer-info fix
  // must not silently reprice the booking against today's catalog and settings.
  if (settlement.priceAffecting) {
    Object.assign(fields, {
      salesTaxPercent: quote.salesTaxPercent,
      bookingPlatformFee: quote.platformFeeCents,
      productPriceAmount: quote.productCostCents,
      cardProcessingFeeAmount: quote.cardProcessingFeeCents,
      salesTaxAmount: quote.salesTaxCents,
      totalAmount: settlement.newTotalAmountCents,
      deliveryCostCents: draft.deliveryNeeded ? quote.deliveryCostCents : 0,
      pickupCostCents: draft.homePickupNeeded ? quote.pickupCostCents : 0,
      deliveryWithDiscount: draft.deliveryNeeded ? quote.deliveryWithDiscountCents : 0,
      pickupWithDiscount: draft.homePickupNeeded ? quote.pickupWithDiscountCents : 0,
      transportationDiscount: {
        discountPercent: quote.transportationDiscountPercent,
        discountName: quote.transportationDiscountName,
        discountCents: quote.transportationDiscountCents,
      },
      durationDiscount: {
        discountPercent: quote.durationDiscountPercent,
        discountName: quote.durationDiscountName,
        discountCents: quote.durationDiscountCents,
      },
    });
  }

  return fields;
};

// ---------------------------------------------------------------------------
// Availability backstop
// ---------------------------------------------------------------------------

/**
 * Items in the draft that exceed what is actually free for the draft's dates,
 * excluding this booking's own footprint. [] when everything fits. Same backstop
 * pattern as the cash desk (CashPayment.js:147) — staff edits REFUSE on
 * shortage rather than oversell, since nothing has been captured yet.
 */
export const findDraftShortages = ({
  draft,
  lineItems,
  bookings,
  pendingBookings,
  effectiveProducts1,
  effectiveProducts2,
  excludeBookingId,
}) => {
  const availability = computeAvailabilityForRange({
    startDate: draft.startDate,
    endDate: draft.endDate,
    bookings,
    pendingBookings,
    products1Catalog: effectiveProducts1,
    products2Catalog: effectiveProducts2,
    excludeBookingId,
  });

  const shortages = [];
  const check = (items, map) =>
    (items || []).forEach((item) => {
      const info = map?.[item.id];
      if (!info?.hasInventory) return; // untracked = unlimited
      if ((item.quantity || 0) > info.available) {
        shortages.push({
          name: item.name || item.id,
          wanted: item.quantity,
          available: info.available,
        });
      }
    });

  check(lineItems.product1, availability.product1);
  check(lineItems.product2, availability.product2);
  return shortages;
};
